import React, { useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import DefaultButton from '../../../global/default-button/default-button';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import OptionsButton from '../../../global/options-button/options-button';
import './control.scss';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { ZoomMeetingModal } from '../shared/connections-list/_zoom-meeting-modal/zoom-meeting-modal.presentation';
import ControlList from './_control-list/_control-list';
import ControlViewModel from './control.viewmodel';
import { OpenFrom } from '../../../../tracking-service/tracking-values';
import { CreateZoomWebinarModal } from '../shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar-modal.presentation';
import { CommonModal } from '../../../global/modal/common/common-modal';
import KnovioPreviewSpinner from '../../../global/spinner/knovio-preview-spinner';
import { KnovioLiveStreamModal } from '../shared/connections-list/knovio-live-stream-modal/knovio-live-stream-modal.presentation';
import { MORE_BUTTON_TEXT } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: ControlViewModel;
    intl: IntlShape;
    handleEventNotesUpdate: (evenNotes: string, openFrom?: OpenFrom) => void;
}

const ControlListPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl } = props;

    useEffect(() => {
        vm.didMount();
        vm.subscribeToEventUpdates();
        vm.getAttendeeDetailsPopoverVM().setClose();
        return () => {
            vm.setFilterDays([]);
            vm.unsubscribeFromEventUpdates();
        };
    }, []);

    const buttonOptions = [
        hasPermission(Permission.EDIT_EVENT) && {
            label: intl.formatMessage({
                id: 'oe.control.editGuidelines',
                defaultMessage: 'Edit Guidelines',
                description: 'Edit general guidelines'
            }),
            action: () => {
                if (!vm.guidelinesFormSubWindow.create || vm.guidelinesFormSubWindow.create.getStatus() === SubWindowStatus.Closed) {
                    vm.editEventGuidelines();
                }
            }
        }
    ].filter(Boolean);

    const shouldRenderKnovioLiveStreamEdit = hasPermission(Permission.EDIT_KNOVIO_LIVE_STREAMS)
        && vm.getConnectionsVM()
        && vm.getConnectionsVM().getKnovioLiveStreamModalVM();

    return (
        <div className='event-page-content'>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        { hasPermission(Permission.VIEW_GUIDELINES) ? (
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={<FormattedMessage
                                    id='oe.control.viewGuidelines'
                                    defaultMessage='View Guidelines'
                                    description='View general guidelines'
                                />}
                                onClick={() => {
                                     if (!vm.guidelinesFormSubWindow.edit || vm.guidelinesFormSubWindow.edit.getStatus() === SubWindowStatus.Closed) {
                                        vm.viewEventGuidelines();
                                    }
                                }}
                            />
                        ) : null}
                        {!!buttonOptions.length && (
                            <OptionsButton
                                modifier={['fat', 'options']}
                                label={intl.formatMessage(MORE_BUTTON_TEXT)}
                                options={buttonOptions}
                            />
                        )}
                    </div>
                </div>
            </section>
            <ControlList controlVM={vm} />
            <ZoomMeetingModal vm={vm.getZoomMeetingVM()} />
            <CreateZoomWebinarModal vm={vm.getWebinarMeetingVM()} />

            {shouldRenderKnovioLiveStreamEdit &&
                <KnovioLiveStreamModal vm={vm.getConnectionsVM().getKnovioLiveStreamModalVM()}/>}
            {shouldRenderKnovioLiveStreamEdit &&
                <CommonModal isOpen={vm.getConnectionsVM().isKnovioPreCheckLoading} className='knovio-preFetch-loader-popover'>
                    <div className='knovio-preFetch-loader-popover__content-wrapper'>
                        <KnovioPreviewSpinner classname='knovioPreCheckLoading'/>
                        <div className='oe-form-field__info'>
                            <FormattedMessage
                                id='oe.knovio.prefetchLoader'
                                defaultMessage='Loading'
                                description='Info text'
                            />
                        </div>
                    </div>
                </CommonModal>}
        </div>
    );
}));

export default ControlListPresentation;
